import { useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { PROJECT_CONTRACTS_QUERY } from "@/hooks/useProjectContracts";
import { PROJECT_FINANCIAL_SUMMARY_QUERY } from "@/hooks/useProjectFinancialSummary";
import type { InstallmentDraft } from "./generateInstallments";

export const CREATE_CONTRACT_MUTATION = gql`
  mutation CreateContract($input: CreateContractInput!) {
    createContract(input: $input) {
      contract {
        id
        name
        totalValue
        numberOfInstallments
        installmentsCount
        startDate
        status
        installments {
          id
          number
          amount
          dueDate
          status
          paidAt
        }
        users {
          id
          fullName
          email
        }
      }
    }
  }
`;

export interface CreateContractInput {
  projectId: string;
  name: string;
  totalValue: string;
  installments: InstallmentDraft[];
  userIds?: string[];
}

export function useCreateContract(projectId: string) {
  const [mutate, { loading, error }] = useMutation(CREATE_CONTRACT_MUTATION, {
    refetchQueries: [
      { query: PROJECT_CONTRACTS_QUERY, variables: { projectId } },
      { query: PROJECT_FINANCIAL_SUMMARY_QUERY, variables: { projectId } },
    ],
  });

  const createContract = async (input: CreateContractInput) => {
    const { data } = await mutate({
      variables: {
        input: {
          projectId: input.projectId,
          name: input.name,
          totalValue: input.totalValue,
          installments: input.installments.map((i) => ({
            number: i.number,
            amount: i.amount.toFixed(2),
            dueDate: i.dueDate,
          })),
          userIds: input.userIds?.length ? input.userIds : null,
        },
      },
    });

    return (data as { createContract?: { contract: unknown } })?.createContract
      ?.contract;
  };

  return { createContract, loading, error };
}
